/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { SseSearchClientFactory } from './factory/sse-search-client-factory';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import * as _ from 'underscore';
import { Logger, MessageService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { NotifyService } from '@app/common/notify.service';
import { SseSearchFilterSortService } from '../search/sse-search-filter-sort.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '@app/common/app-config.service';
import { Subject, Subscription } from 'rxjs';
import { CacheFactoryService } from '@app/ui/client-cache/factory/cache-factory.service';
import { WorkItemService } from '@app/data-model/item/work-item.service';
import { UtilService } from '@app/util/util.service';
import { Queue } from '@core/lib/data-structures/Queue';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';
import { RemoteArchivesService } from '@app/ui/search/remote-archives.service';

const log = new Logger('SseSearchService');

@Injectable({
  providedIn: 'root'
})
export class SseSearchService {
  private numberOfRetrievedXEExams = 0;
  private MAX_EXAM_DISPLAY_LIMIT = 501;
  private readonly sseSearchClientFactory: SseSearchClientFactory;
  private sseSearchResultsUpdateListener;
  private remoteSearchResultsObserver: Subject<any> = new Subject();
  private sseQueue = new Queue();

  constructor(
    private cacheFactoryService: CacheFactoryService,
    private dataService: ProxyDataService,
    private notifyService: NotifyService,
    private filterAndSortService: SseSearchFilterSortService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private appConfigService: AppConfigService,
    private workItemService: WorkItemService,
    private utilService: UtilService,
    private remoteArchivesService: RemoteArchivesService
  ) {
    this.sseSearchClientFactory = this.cacheFactoryService.createSSESearchClientCacheFactory('sseStudySearch');
  }
  cachedData = {};
  searchStatus;
  studySearchUpdateEvent = 'studySearchUpdate';
  subscribers = [];
  CHANNEL_NAME = 'STUDY_SEARCH';
  GLOBAL_SEARCH_URL = 'global-search/exams';
  prefixRegex = /[%|*]$/;
  suffixRegex = /^[%|*]/;
  WILDCARD = '*';
  initialSearchCriteria;
  currentSearchType;

  getSseSearchClientFactory() {
    return this.sseSearchClientFactory;
  }

  getRemoteSearchResultsObserver() {
    return this.remoteSearchResultsObserver.asObservable();
  }

  populateNextPage(list) {
    this.clearIndicatorModel();
    const searchCriteriaInfo: SearchParams = list.searchCriteriaInfo;
    if (this.utilService.isInvalidInput(searchCriteriaInfo)) {
      log.error(`quick remote search - invalid search criteria ${JSON.stringify(list.searchCriteriaInfo)}`);
      this.applyRemoteSearchResults('invalidSearchInput');
      return;
    }
    this.numberOfRetrievedXEExams = 0;
    this.unsubscribeAllEvents();

    if (
      (searchCriteriaInfo.isRemoteSearch() || this.isLastSearchPartial() || list.isGlobal) &&
      !this.isInlineFilterOrSortApplied(searchCriteriaInfo)
    ) {
      this.setCurrentSearchType('SearchRemotePatient');
      this.setInitialSearchCriteria(searchCriteriaInfo);
      this.subscribeEvents(searchCriteriaInfo);
      this.sseSearchResultsUpdateListener = this.onSSESearchResultsUpdate(this.searchResultsResponseHandler);
      this.getSSESearchResults(searchCriteriaInfo).catch(ex => {
        log.error('Error occurred while getting remote quick search', JSON.stringify(ex));
        this.unsubscribeAllEvents();
      });
    } else {
      this.applyRemoteSearchResults('invalidSearchInput');
    }
  }

  /**
   * @name getSSESearchResults
   * @desc Fetches search results from XE
   * @param {Object} searchCriteriaInfo
   * @param isStreaming
   */
  getSSESearchResults(searchCriteriaInfo: SearchParams) {
    const params = this.buildServiceParams(searchCriteriaInfo);
    return this.dataService.get(this.GLOBAL_SEARCH_URL, params);
  }

  /**
   * @name onSSESearchResultsUpdate
   * @desc Adds listener for when exams are added
   * @param {function} callback. Handler for adding exams in page
   * @param {object} context. Optional Context object to use when calling the handler.
   */
  onSSESearchResultsUpdate(callback, context?): any {
    log.debug('In onSSESearchResultsUpdate');
    return this.sseSearchClientFactory.on(this.studySearchUpdateEvent, callback, context);
  }

  subscribeEvents(searchCriteriaInfo) {
    const messageSubscriberInfo = {
      id: searchCriteriaInfo.searchTimeStamp,
      instance: this.subscribeMessageEvent(searchCriteriaInfo)
    };

    const closeSubscriberInfo = {
      id: searchCriteriaInfo.searchTimeStamp,
      instance: this.subscribeCloseEvent(searchCriteriaInfo)
    };
    const errorSubscriberInfo = {
      id: searchCriteriaInfo.searchTimeStamp,
      instance: this.subscribeErrorEvent(searchCriteriaInfo)
    };

    this.subscribers.push(messageSubscriberInfo);
    this.subscribers.push(closeSubscriberInfo);
    this.subscribers.push(errorSubscriberInfo);

    log.debug('sse-search-service -- Events subscribed.');
  }

  /**
   * Unsubscribe all the subsribers used for XE search (Message, Close and Error)
   * and reset them to empty
   */
  unsubscribeAllEvents() {
    this.sseSearchResultsUpdateListener && this.sseSearchResultsUpdateListener.unregister();
    if (!_.isEmpty(this.subscribers)) {
      _.each(this.subscribers, subscriber => {
        subscriber.instance && subscriber.instance.unsubscribe();
      });
      this.subscribers = [];
    }
  }

  isLoading() {
    return this.searchStatus === appConstants.XE_SEARCH_RESPONSE_CODES.XE_WAITING_FOR_DATA;
  }

  setInitialSearchCriteria(searchCriteriaInfo) {
    this.initialSearchCriteria = searchCriteriaInfo;
  }

  getInitialSearchCriteria() {
    return this.initialSearchCriteria;
  }

  setCurrentSearchType(searchType) {
    this.currentSearchType = searchType;
  }

  getCurrentSearchType() {
    return this.currentSearchType;
  }

  cacheSearchResults(data) {
    this.cachedData = data;
  }

  getCachedSearchResults() {
    return this.cachedData;
  }

  /**
   * Function to sort and filter search result
   * @param filterAndSortCriteria
   * @returns {*}
   */
  getFilteredAndSortedResults(filterAndSortCriteria) {
    return this.filterAndSortService.getFilteredAndSortedResults(this.cachedData, filterAndSortCriteria);
  }

  /************************** Private Functions **************************/

  /**
   * subscribeMessageEvent will be invoked only when we get the Success response from XE.
   *
   * This subscriber is not managed by ngToolbelt.
   */
  subscribeMessageEvent(searchCriteriaInfo): Subscription {
    log.debug('In search sse subscribeMessageEvent');
    return this.messageService
      .on('STUDY_SEARCH.MESSAGE.FOR.STUDY_SEARCH_' + searchCriteriaInfo.searchTimeStamp)
      .subscribe(data => {
        log.debug('Event STUDY_SEARCH.MESSAGE.FOR -> ' + JSON.stringify(data.requestParameters));
        this.sseSearchClientFactory.updateSearchResults(searchCriteriaInfo, data, this.studySearchUpdateEvent);
        this.updateSearchStatus(data);
      });
  }

  /**
   * subscribeCloseEvent will be invoked when Streaming response stopped response from XE.
   */

  subscribeCloseEvent(searchCriteriaInfo): Subscription {
    return this.messageService
      .on('STUDY_SEARCH.CLOSE.FOR.STUDY_SEARCH_' + searchCriteriaInfo.searchTimeStamp)
      .subscribe(data => {
        log.debug('Event STUDY_SEARCH.CLOSE.FOR -> ' + JSON.stringify(data.requestParameters));
        this.handleCloseEvent();
      });
  }

  /**
   * subscribeCloseEvent will be invoked when error response received from XE.
   */
  subscribeErrorEvent(searchCriteriaInfo): Subscription {
    return this.messageService
      .on('STUDY_SEARCH.ERROR.FOR.STUDY_SEARCH_' + searchCriteriaInfo.searchTimeStamp)
      .subscribe(data => {
        log.error('Event STUDY_SEARCH.ERROR.FOR -> ' + JSON.stringify(data.requestParameters));
        this.handleErrorEvent();
      });
  }

  handleCloseEvent() {
    this.unsubscribeAllEvents();
    log.info('sse-search-service -- Closed event received');
  }

  handleErrorEvent() {
    this.unsubscribeAllEvents();
    log.error('sse-search-service -- Error event received');
    this.notifyService.error(this.translateService.instant('xeServiceDown.examsMsg'));
  }

  updateSearchStatus(data) {
    this.searchStatus = data.finalResponseStatus;
  }

  buildServiceParams(searchCriteriaInfo: SearchParams) {
    let params: any = {};
    if (searchCriteriaInfo) {
      // if search criteria has isGlobal flag set to true, then add isGlobal flag and searchTimeStamp to params
      if (searchCriteriaInfo.isGlobalWorklist) {
        params.isGlobalWorklist = String(true);
        searchCriteriaInfo.repositoryList = this.remoteArchivesService.allRepositories?.filter(
          repo => repo.type !== 'XED'
        );
        params = this.updateWithRepository(params, searchCriteriaInfo);
      } else {
        if (searchCriteriaInfo.isRemoteSearch()) {
          params = this.getSearchParam(searchCriteriaInfo);
        }
        params = this.updateWithDateToken(params, searchCriteriaInfo);
        params = this.updateWithRepository(params, searchCriteriaInfo);
        params.maxResults = this.appConfigService.getRemoteSearchMaxResultsCount();
      }
      if (searchCriteriaInfo.searchTimeStamp) {
        params.searchTimeStamp = searchCriteriaInfo.searchTimeStamp;
      }
    }
    return params;
  }

  getSearchParam(searchCriteriaInfo: SearchParams) {
    const param: any = {};
    if (searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.MRN) {
      param.mrn = encodeURIComponent(this.buildServiceParamsWithWildcard(searchCriteriaInfo.mrn));
    } else if (searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.ACCESSION) {
      param.accessionNumber = encodeURIComponent(this.buildServiceParamsWithWildcard(searchCriteriaInfo.accession));
    } else if (searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.PATIENT_NAME) {
      param.patientName = encodeURIComponent(searchCriteriaInfo.patientName);
    }

    return param;
  }

  buildServiceParamsWithWildcard(searchCriteria) {
    const isPrefix = this.prefixRegex.test(searchCriteria);
    const isSuffix = this.suffixRegex.test(searchCriteria);
    if (isPrefix) {
      const lastIndex = searchCriteria.length - 1;
      return searchCriteria.trim().substring(0, lastIndex) + this.WILDCARD;
    }
    if (isSuffix) {
      return this.WILDCARD + searchCriteria.trim().substring(1);
    }
    return searchCriteria;
  }

  updateWithRepository(params, searchCriteriaInfo: SearchParams) {
    // repository info
    if (searchCriteriaInfo.repositoryList && searchCriteriaInfo.repositoryList.length) {
      const repoParam = this.getRepositoryParam(searchCriteriaInfo);
      if (repoParam) {
        params.repositoryList = repoParam;
      }
    }

    return params;
  }

  updateWithDateToken(params, searchCriteriaInfo: SearchParams) {
    if (
      searchCriteriaInfo.timeOfExam &&
      searchCriteriaInfo.timeOfExam.option > 0 &&
      searchCriteriaInfo.timeOfExam.option < 16
    ) {
      params.studyDateRange = appConstants.datePickerRangeValues[searchCriteriaInfo.timeOfExam.option];
    }
    return params;
  }

  getRepositoryParam(searchCriteriaInfo) {
    const repoInfo = [];
    _.each(searchCriteriaInfo.repositoryList, repository => {
      if (repository.id && repository.type) {
        repoInfo.push(repository.type + '|' + repository.id);
      }
    });
    if (repoInfo.length) {
      return repoInfo.join(',');
    }
  }

  private searchResultsResponseHandler = (searchCriteriaInfo, data) => {
    log.debug('search-remote-patient-list -- SSE data received');
    this.updateIndicatorModel(data);
    this.sseQueue.enqueue({
      searchCriteriaInfo,
      data
    });
    if (this.sseQueue.size() === 1) {
      this.processSSEQueue({ searchCriteriaInfo, data });
    }
  };

  private updateIndicatorModel(data) {
    this.messageService.broadcast('updateSearchResultsIndicatorModel', data);
  }

  private clearIndicatorModel() {
    this.messageService.broadcast('clearSearchResultsIndicatorModel');
  }

  private applyRemoteSearchResults(error: any, data: any = undefined, items?: any[]) {
    log.info(`applyRemoteSearchResults, isErrored - ${error}, Result(Items length) - ${items && items.length}`);
    this.sseQueue.dequeue();
    if (this.isNotWaitingForStreaming(data)) {
      this.unsubscribeAllEvents();
      this.numberOfRetrievedXEExams = 0;
    }
    if (
      error === false &&
      this.isStillStreaming(data.finalResponseStatus) &&
      data.studies &&
      data.studies.length <= 0
    ) {
      log.info(
        `applyRemoteSearchResults - skip updating remote search results - Number of studies retrieved ${
          data.studies && data.studies.length
        }`
      );
    } else {
      this.remoteSearchResultsObserver.next({
        error: error,
        items
      });
    }
    if (this.sseQueue.canDequeue()) {
      this.processSSEQueue(this.sseQueue.get(0));
    }
  }

  private processSSEQueue({ searchCriteriaInfo, data }) {
    if (searchCriteriaInfo) {
      if (this.isPageUpdateNeeded(searchCriteriaInfo, data, this.numberOfRetrievedXEExams)) {
        this.numberOfRetrievedXEExams = data.studies.length;
        if (!this.isInlineFilterOrSortApplied(searchCriteriaInfo)) {
          this.cacheSearchResults(data);
        }

        let displayStudies: any[];
        if (this.isInlineFilterOrSortApplied(searchCriteriaInfo)) {
          displayStudies = this.getStudiesBasedOnDisplayLimit(data.studies, searchCriteriaInfo.isGlobalWorklist);
        } else {
          displayStudies = this.sortStudiesByStudyTime(data.studies);
          displayStudies = this.getStudiesBasedOnDisplayLimit(displayStudies, searchCriteriaInfo.isGlobalWorklist);
        }
        if (displayStudies && displayStudies.length) {
          const workItems = this.workItemService.createWorkItems(
            'SearchList',
            this.getPagesForRemoteSearch(displayStudies),
            undefined
          );
          if (workItems && workItems.length) {
            if (data.siteDetails && data.siteDetails.Local === 'XER_STUDY_SEARCH_SUCCESS') {
              this.workItemService
                .getIconDetailsForWorkItems(workItems)
                .then((data: { list: any; updates: Promise<any> }) => {
                  if (data && data.updates instanceof Promise) {
                    data.updates
                      .then(updates => {
                        updates.protocolWorklist = false;
                        this.workItemService.updateFromMap(workItems, updates);
                        this.applyRemoteSearchResults(false, data, workItems);
                      })
                      .catch(error => {
                        log.error('There was a problem retrieving Work Item States and Indicators', error);
                        this.applyRemoteSearchResults(true, data);
                      });
                  } else {
                    this.applyRemoteSearchResults(false, data, workItems);
                  }
                });
            } else {
              this.applyRemoteSearchResults(false, data, workItems);
            }
          } else {
            this.applyRemoteSearchResults(false, data, data && data.items);
          }
        } else {
          this.applyRemoteSearchResults(false, data);
        }
      } else {
        this.applyRemoteSearchResults(false, data);
        log.info('search-remote-patient-list -- Resolving with blank page');
      }
    } else {
      this.applyRemoteSearchResults(false, data);
    }
  }

  private isInlineFilterOrSortApplied(searchCriteriaInfo) {
    return (
      !!searchCriteriaInfo.filterParams &&
      (searchCriteriaInfo.filterParams.filter || searchCriteriaInfo.filterParams.sort)
    );
  }

  private isNotWaitingForStreaming(data) {
    return (
      !!data &&
      !!data.finalResponseStatus &&
      data.finalResponseStatus !== appConstants.XE_SEARCH_RESPONSE_CODES.XE_WAITING_FOR_DATA
    );
  }

  private getPagesForRemoteSearch(data) {
    const uniquePatients = [];
    for (let i = 0; i < data.length; ++i) {
      const item = data[i];
      const workItem = item.workItem ? item.workItem : item;
      const isPatientAlreadyPresent = element => {
        return this.isPatientMatching(workItem, element);
      };
      if (!_.some(uniquePatients, isPatientAlreadyPresent)) {
        uniquePatients.push({
          patientId: workItem.patientId,
          patientName: workItem.patientName.text,
          repositoryId: workItem.repositoryId,
          patientBirthDate: workItem.patientBirthDate
        });
      }
    }
    return this.getWorkItemsGroupedByPatient(uniquePatients, data);
  }

  private getWorkItemsGroupedByPatient(patients, wItems) {
    const groupedByPatient = [];
    for (let j = 0; j < patients.length; ++j) {
      const patient = patients[j];
      let firstItem = true;
      for (let k = 0; k < wItems.length; ++k) {
        const item = wItems[k];
        const workItem = item.workItem ? item.workItem : item;
        if (this.isPatientMatching(workItem, patient)) {
          if (firstItem) {
            workItem.displayPatientNameHeader = true;
          } else {
            workItem.displayPatientNameHeader = false;
          }
          firstItem = false;
          groupedByPatient.push(item);
        }
      }
    }

    return groupedByPatient;
  }

  private isPatientMatching(wItem, patientToCompare) {
    const isMatching =
      wItem.patientId === patientToCompare.patientId &&
      wItem.patientName.text === patientToCompare.patientName &&
      wItem.patientBirthDate === patientToCompare.patientBirthDate &&
      wItem.repositoryId === patientToCompare.repositoryId;

    return isMatching;
  }

  private sortStudiesByStudyTime(studies) {
    const sortedStudies = _.sortBy(studies, study => {
      return -new Date(this.formatDate(study.workItem.procedureStartTime));
    });
    return sortedStudies;
  }

  private formatDate(procedureStartTime) {
    const dateArr = procedureStartTime.split('.');
    const dateTime = dateArr[0] ? dateArr[0].split(' ').join('T') : undefined;
    let milliseconds = dateArr[1] ? dateArr[1] : '000';
    let formattedDate;
    if (dateTime) {
      for (let i = milliseconds.length; i < 3; i++) {
        milliseconds = milliseconds + '0';
      }
      formattedDate = [dateTime, milliseconds].join('.');
    }
    return formattedDate;
  }

  private getStudiesBasedOnDisplayLimit(studies, skipGlobalExamLimitCheck) {
    // Display only exams based on display limit. The display limit for global worklist is set from worklist definition.
    if (!skipGlobalExamLimitCheck && _.size(studies) > this.MAX_EXAM_DISPLAY_LIMIT) {
      studies = _.first(studies, this.MAX_EXAM_DISPLAY_LIMIT);
    }
    return studies;
  }

  private isPageUpdateNeeded(searchCriteriaInfo, data, numberOfRetrievedXEExams) {
    return (
      searchCriteriaInfo.searchType === 'RemoteExamSearch' ||
      (this.areStudiesPresent(data) &&
        this.isUpdateNeeded(data, numberOfRetrievedXEExams) &&
        this.isStillStreamingOrComplete(data.finalResponseStatus)) ||
      this.isPartialSuccessWithMaxResults(data)
    );
  }

  private isLastSearchPartial() {
    const cachedSearchResults: any = this.getCachedSearchResults();
    return cachedSearchResults.finalResponseStatus === appConstants.XE_SEARCH_RESPONSE_CODES.XE_PARTIAL_SUCCESS;
  }

  private areStudiesPresent(data) {
    return data && data.studies && data.studies.length > 0;
  }

  private isUpdateNeeded(data, numberOfRetrievedXEExams) {
    return data.studies.length >= numberOfRetrievedXEExams;
  }

  private isStreamingComplete(status) {
    return status === appConstants.XE_SEARCH_RESPONSE_CODES.XE_SUCCESS;
  }

  private isStillStreaming(status) {
    return status === appConstants.XE_SEARCH_RESPONSE_CODES.XE_WAITING_FOR_DATA;
  }

  private isPartialSuccess(status) {
    return status === appConstants.XE_SEARCH_RESPONSE_CODES.XE_PARTIAL_SUCCESS;
  }

  private isStillStreamingOrComplete(status) {
    return this.isStillStreaming(status) || this.isStreamingComplete(status);
  }

  private isPartialSuccessWithMaxResults(data) {
    return (
      this.isPartialSuccess(data.finalResponseStatus) ||
      (!!data.studies && data.studies.length >= parseInt(this.appConfigService.getRemoteSearchMaxResultsCount()))
    );
  }
}
